import { ColumnDef } from "@tanstack/react-table";
import {
	moneyFormat,
	enumToStringConverter,
} from "../../../helpers/appHelpers";

// Type definitions
interface Currency {
	code: string;
}

interface CompanyCurrency {
	currency: Currency;
}

interface BenefitType {
	name: string;
}

interface AllowanceGroupRow {
	id: number;
	name: string;
	code: string;
	description: string;
	formulaType: string;
	active: boolean;
	minValue: number;
	maxValue: number;
	prorate: boolean;
	minEmploymentPeriod?: number;
	employmentPeriodUnit?: string;
	paymentFrequency?: string;
	paymentInstance?: number;
	applicableValuePeriod: number;
	nonTaxable: boolean;
	companyCurrency?: CompanyCurrency;
	createdAt?: string;
	modifiedAt?: string;
}

interface BenefitGroupRow {
	id: number;
	name: string;
	code: string;
	description: string;
	benefitTypeInstance: BenefitType;
	taxCode?: string;
	frequency?: string;
	createdAt?: string;
	modifiedAt?: string;
}

interface LoanBenefitGroupRow {
	id: number;
	name: string;
	code: string;
	description: string;
	centralBankInterestRate: number;
	employerInterestRate: number;
	loanTenor: number;
	floor: number | null;
	ceiling: number | null;
	createdAt?: string;
	modifiedAt?: string;
}

// Helper function to format applicable value period
const formatApplicableValuePeriod = (period: number): string => {
	switch (period) {
		case 0:
			return "Month";
		case 1:
			return "Year";
		case 2:
			return "Quarter";
		default:
			return "--";
	}
};

// ========== Allowance Groups Columns ==========
export const allowanceGroupColumns: ColumnDef<AllowanceGroupRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "formulaType",
		accessorKey: "formulaType",
		header: "Formula Type",
		size: 180,
		cell: ({ row }) => enumToStringConverter(row.original.formulaType),
	},
	{
		id: "active",
		accessorKey: "active",
		header: "Active",
		size: 140,
		cell: ({ row }) => (row.original.active ? "Yes" : "No"),
	},
	{
		id: "minValue",
		accessorKey: "minValue",
		header: "Min Value",
		size: 180,
		cell: ({ row }) => {
			const amount = row.original.minValue;
			const currency = row.original.companyCurrency?.currency;
			return `${currency?.code || ""} ${moneyFormat(amount)}`;
		},
	},
	{
		id: "maxValue",
		accessorKey: "maxValue",
		header: "Max Value",
		size: 180,
		cell: ({ row }) => {
			const amount = row.original.maxValue;
			const currency = row.original.companyCurrency?.currency;
			return `${currency?.code || ""} ${moneyFormat(amount)}`;
		},
	},
	{
		id: "prorate",
		accessorKey: "prorate",
		header: "Prorate",
		size: 140,
		cell: ({ row }) => (row.original.prorate ? "Yes" : "No"),
	},
	{
		id: "minEmploymentPeriod",
		accessorKey: "minEmploymentPeriod",
		header: "Min Employment Period",
		size: 220,
		cell: ({ row }) => {
			const period = row.original.minEmploymentPeriod;
			const unit = row.original.employmentPeriodUnit;
			return period && unit
				? `${period} ${enumToStringConverter(unit)}(s)`
				: "--";
		},
	},
	{
		id: "paymentFrequency",
		accessorKey: "paymentFrequency",
		header: "Payment Frequency",
		size: 200,
		cell: ({ row }) =>
			row.original.paymentFrequency
				? enumToStringConverter(row.original.paymentFrequency)
				: "--",
	},
	{
		id: "paymentInstance",
		accessorKey: "paymentInstance",
		header: "Payment Instance",
		size: 180,
		cell: ({ row }) => row.original.paymentInstance || "--",
	},
	{
		id: "applicableValuePeriod",
		accessorKey: "applicableValuePeriod",
		header: "Applicable Value Period",
		size: 220,
		cell: ({ row }) =>
			formatApplicableValuePeriod(row.original.applicableValuePeriod),
	},
	{
		id: "nonTaxable",
		accessorKey: "nonTaxable",
		header: "Non-Taxable",
		size: 140,
		cell: ({ row }) => (row.original.nonTaxable ? "Yes" : "No"),
	},
];

export const allowanceGroupColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	formulaType: true,
	active: true,
	minValue: false,
	maxValue: false,
	prorate: true,
	minEmploymentPeriod: false,
	paymentFrequency: true,
	paymentInstance: false,
	applicableValuePeriod: true,
	nonTaxable: true,
};

// ========== Benefit Groups Columns ==========
export const benefitGroupColumns: ColumnDef<BenefitGroupRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "benefitTypeInstance",
		accessorKey: "benefitTypeInstance",
		header: "Benefit Type",
		size: 250,
		cell: ({ row }) => row.original.benefitTypeInstance?.name || "--",
	},
	{
		id: "taxCode",
		accessorKey: "taxCode",
		header: "Tax Code",
		size: 180,
		cell: ({ row }) => row.original.taxCode || "--",
	},
	{
		id: "frequency",
		accessorKey: "frequency",
		header: "Frequency",
		size: 180,
		cell: ({ row }) =>
			row.original.frequency
				? enumToStringConverter(row.original.frequency)
				: "--",
	},
];

export const benefitGroupColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	benefitTypeInstance: true,
	taxCode: false,
	frequency: false,
};

// ========== Loan Benefit Groups Columns ==========
export const loanBenefitGroupColumns: ColumnDef<LoanBenefitGroupRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "centralBankInterestRate",
		accessorKey: "centralBankInterestRate",
		header: "Central Bank Interest Rate",
		size: 230,
		cell: ({ row }) =>
			(Number(row.original.centralBankInterestRate) * 100).toFixed(2) + "%",
	},
	{
		id: "employerInterestRate",
		accessorKey: "employerInterestRate",
		header: "Employer Interest Rate",
		size: 220,
		cell: ({ row }) =>
			(Number(row.original.employerInterestRate) * 100).toFixed(2) + "%",
	},
	{
		id: "loanTenor",
		accessorKey: "loanTenor",
		header: "Loan Tenor",
		size: 180,
		cell: ({ row }) => `${row.original.loanTenor} Months`,
	},
	{
		id: "floor",
		accessorKey: "floor",
		header: "Floor",
		size: 140,
		cell: ({ row }) =>
			row.original.floor ? moneyFormat(row.original.floor) : "--",
	},
	{
		id: "ceiling",
		accessorKey: "ceiling",
		header: "Ceiling",
		size: 140,
		cell: ({ row }) =>
			row.original.ceiling ? moneyFormat(row.original.ceiling) : "--",
	},
];

export const loanBenefitGroupColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	centralBankInterestRate: true,
	employerInterestRate: true,
	loanTenor: true,
	floor: false,
	ceiling: false,
};

interface AllowanceRow {
	id: number;
	name: string;
	code: string;
	description: string;
	formulaType: string;
	fixedComponent: number;
	rateComponent: number;
	active: boolean;
	minValue: number;
	maxValue: number;
	prorate: boolean;
	minEmploymentPeriod?: number;
	employmentPeriodUnit?: string;
	paymentFrequency?: string;
	paymentInstance?: number;
	applicableValuePeriod: number;
	nonTaxable: boolean;
	companyCurrency?: CompanyCurrency;
	group?: { name: string };
	employeeAllowances?: Array<{
		employee: { firstName: string; lastName: string };
	}>;
	payPeriods?: Array<{ code: string }>;
	createdAt?: string;
	modifiedAt?: string;
}

export const allowanceColumns: ColumnDef<AllowanceRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "allowanceGroup",
		accessorKey: "group.name",
		header: "Allowance Group",
		size: 200,
		cell: ({ row }) => row.original.group?.name || "--",
	},
	{
		id: "formulaType",
		accessorKey: "formulaType",
		header: "Formula Type",
		size: 180,
		cell: ({ row }) => enumToStringConverter(row.original.formulaType),
	},
	{
		id: "fixedComponent",
		accessorKey: "fixedComponent",
		header: "Fixed Component",
		size: 180,
		cell: ({ row }) => {
			const amount = row.original.fixedComponent;
			const currency = row.original.companyCurrency?.currency;
			return `${currency?.code || ""} ${moneyFormat(amount)}`;
		},
	},
	{
		id: "rateComponent",
		accessorKey: "rateComponent",
		header: "Rate Component",
		size: 180,
		cell: ({ row }) =>
			`${(Number(row.original.rateComponent) * 100).toFixed(2)}%`,
	},
	{
		id: "employees",
		accessorKey: "employeeAllowances",
		header: "Assigned Employees",
		size: 280,
		cell: ({ row }) => {
			const allowances = row.original.employeeAllowances;
			if (!allowances?.length) return "--";
			return (
				<div className="flex flex-wrap gap-1">
					{allowances.map((ea: any, index: number) => (
						<span
							key={index}
							className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700"
						>
							{ea.employee?.firstName} {ea.employee?.lastName}
						</span>
					))}
				</div>
			);
		},
	},
	{
		id: "payPeriods",
		accessorKey: "payPeriods",
		header: "Pay Periods",
		size: 200,
		cell: ({ row }) => {
			const periods = row.original.payPeriods;
			return periods && periods.length > 0
				? periods.map((p) => p.code).join(", ")
				: "--";
		},
	},
	{
		id: "active",
		accessorKey: "active",
		header: "Active",
		size: 140,
		cell: ({ row }) => (row.original.active ? "Yes" : "No"),
	},
	{
		id: "minValue",
		accessorKey: "minValue",
		header: "Min Value",
		size: 180,
		cell: ({ row }) => {
			const amount = row.original.minValue;
			const currency = row.original.companyCurrency?.currency;
			return `${currency?.code || ""} ${moneyFormat(amount)}`;
		},
	},
	{
		id: "maxValue",
		accessorKey: "maxValue",
		header: "Max Value",
		size: 180,
		cell: ({ row }) => {
			const amount = row.original.maxValue;
			const currency = row.original.companyCurrency?.currency;
			return `${currency?.code || ""} ${moneyFormat(amount)}`;
		},
	},
	{
		id: "prorate",
		accessorKey: "prorate",
		header: "Prorate",
		size: 140,
		cell: ({ row }) => (row.original.prorate ? "Yes" : "No"),
	},
	{
		id: "nonTaxable",
		accessorKey: "nonTaxable",
		header: "Non-Taxable",
		size: 140,
		cell: ({ row }) => (row.original.nonTaxable ? "Yes" : "No"),
	},
	{
		id: "applicableValuePeriod",
		accessorKey: "applicableValuePeriod",
		header: "Applicable Value Period",
		size: 220,
		cell: ({ row }) =>
			formatApplicableValuePeriod(row.original.applicableValuePeriod),
	},
];

export const allowanceColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	allowanceGroup: true,
	formulaType: true,
	fixedComponent: true,
	rateComponent: true,
	employees: true,
	payPeriods: false,
	active: true,
	minValue: false,
	maxValue: false,
	prorate: false,
	nonTaxable: true,
	applicableValuePeriod: true,
};

// Benefit columns for employee benefits
interface BenefitRow {
	id: number;
	name: string;
	code: string;
	description: string;
	benefitTypeInstance?: BenefitType;
	value?: number;
	taxCode?: string;
	frequency?: string;
	group?: { name: string };
	employeeBenefits?: Array<{
		employee: { firstName: string; lastName: string };
	}>;
	payPeriods?: Array<{ code: string }>;
	createdAt?: string;
	modifiedAt?: string;
}

export const benefitColumns: ColumnDef<BenefitRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "benefitGroup",
		accessorKey: "group.name",
		header: "Benefit Group",
		size: 200,
		cell: ({ row }) => row.original.group?.name || "--",
	},
	{
		id: "benefitTypeInstance",
		accessorKey: "benefitTypeInstance",
		header: "Benefit Type",
		size: 250,
		cell: ({ row }) => row.original.benefitTypeInstance?.name || "--",
	},
	{
		id: "value",
		accessorKey: "value",
		header: "Value",
		size: 180,
		cell: ({ row }) =>
			row.original.value ? moneyFormat(row.original.value) : "--",
	},
	{
		id: "taxCode",
		accessorKey: "taxCode",
		header: "Tax Code",
		size: 180,
		cell: ({ row }) => row.original.taxCode || "--",
	},
	{
		id: "frequency",
		accessorKey: "frequency",
		header: "Frequency",
		size: 180,
		cell: ({ row }) =>
			row.original.frequency
				? enumToStringConverter(row.original.frequency)
				: "--",
	},
	{
		id: "employees",
		accessorKey: "employeeBenefits",
		header: "Assigned Employees",
		size: 280,
		cell: ({ row }) => {
			const benefits = row.original.employeeBenefits;
			if (!benefits?.length) return "--";
			return (
				<div className="flex flex-wrap gap-1">
					{benefits.map((eb: any, index: number) => (
						<span
							key={index}
							className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700"
						>
							{eb.employee?.firstName} {eb.employee?.lastName}
						</span>
					))}
				</div>
			);
		},
	},
	{
		id: "payPeriods",
		accessorKey: "payPeriods",
		header: "Pay Periods",
		size: 200,
		cell: ({ row }) => {
			const periods = row.original.payPeriods;
			return periods && periods.length > 0
				? periods.map((p) => p.code).join(", ")
				: "--";
		},
	},
];

export const benefitColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	benefitGroup: true,
	benefitTypeInstance: true,
	value: true,
	taxCode: false,
	frequency: true,
	employees: true,
	payPeriods: false,
};

// Loan Benefit columns for employee loan benefits
interface LoanBenefitRow {
	id: number;
	name: string;
	code: string;
	description: string;
	centralBankInterestRate: number;
	employerInterestRate: number;
	loanTenor: number;
	floor: number | null;
	ceiling: number | null;
	group?: { name: string };
	employeeLoanBenefits?: Array<{
		employee: { firstName: string; lastName: string };
	}>;
	createdAt?: string;
	modifiedAt?: string;
}

export const loanBenefitColumns: ColumnDef<LoanBenefitRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "loanBenefitGroup",
		accessorKey: "group.name",
		header: "Loan Benefit Group",
		size: 200,
		cell: ({ row }) => row.original.group?.name || "--",
	},
	{
		id: "centralBankInterestRate",
		accessorKey: "centralBankInterestRate",
		header: "Central Bank Interest Rate",
		size: 230,
		cell: ({ row }) =>
			(Number(row.original.centralBankInterestRate) * 100).toFixed(2) + "%",
	},
	{
		id: "employerInterestRate",
		accessorKey: "employerInterestRate",
		header: "Employer Interest Rate",
		size: 220,
		cell: ({ row }) =>
			(Number(row.original.employerInterestRate) * 100).toFixed(2) + "%",
	},
	{
		id: "loanTenor",
		accessorKey: "loanTenor",
		header: "Loan Tenor",
		size: 180,
		cell: ({ row }) => `${row.original.loanTenor} Months`,
	},
	{
		id: "employees",
		accessorKey: "employeeLoanBenefits",
		header: "Assigned Employees",
		size: 280,
		cell: ({ row }) => {
			const loanBenefits = row.original.employeeLoanBenefits;
			if (!loanBenefits?.length) return "--";
			return (
				<div className="flex flex-wrap gap-1">
					{loanBenefits.map((elb: any, index: number) => (
						<span
							key={index}
							className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700"
						>
							{elb.employee?.firstName} {elb.employee?.lastName}
						</span>
					))}
				</div>
			);
		},
	},
	{
		id: "floor",
		accessorKey: "floor",
		header: "Floor",
		size: 140,
		cell: ({ row }) =>
			row.original.floor ? moneyFormat(row.original.floor) : "--",
	},
	{
		id: "ceiling",
		accessorKey: "ceiling",
		header: "Ceiling",
		size: 140,
		cell: ({ row }) =>
			row.original.ceiling ? moneyFormat(row.original.ceiling) : "--",
	},
];

export const loanBenefitColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	loanBenefitGroup: true,
	centralBankInterestRate: true,
	employerInterestRate: true,
	loanTenor: true,
	employees: true,
	floor: false,
	ceiling: false,
};
